import React, { useState, useEffect, useRef } from "react";
import { Form, Button, message } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { FaGoogle } from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import styled from "styled-components";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { AuthLayout } from "@/shared/layout";
import {
	EmailStep,
	CodeVerificationStep,
	UsernameStep,
	SocialConnectStep,
	TikTokAuthStep,
	PasswordSetupStep,
	UploadAvatarStep,
	BankDetailsStep,
} from "@/features/auth";
import { userService } from "@/api/user";
import { creatorInvitationApi } from "@/api/creatorInvitation";
import { useAuth } from "@/shared/contexts/AuthContext";
import { CURRENT_TOS_VERSION } from "@/shared/utils/terms";
import { AnimatePresence, motion } from "framer-motion";
import type { BankDetailsValues } from "@/features/auth";
import { markCreatorWelcomePending } from "@/features/creator-onboarding";
import { normalizeEmail } from "@/shared/utils/email";

type PendingCreatorRegistration = {
	email: string;
	password: string;
	name: string;
	roles: string[];
	profileImage?: string;
	tosAccepted: boolean;
	tosVersion: string;
	documentTypes: Array<"terms_of_service" | "fee_schedule" | "privacy_policy">;
};
const StyledForm = styled(Form)`
	height: auto;

	.ant-form-item {
		margin-bottom: 1.25rem;
	}

	.ant-form-item-label {
		padding-bottom: 0.5rem;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #333;
	}

	.ant-input-affix-wrapper {
		height: 47px;
		border-radius: 50px;
		border: 1px solid #d9d9d9;

		&:hover,
		&:focus {
			border-color: ${CUSTOM_COLORS.PRIMARY};
		}
	}

	.ant-input {
		font-size: 0.9rem;
	}
`;

const StepTransition = styled(motion.div)`
	width: 100%;
`;

/** Figma: back chevron ~8×14, 2px rounded stroke */
const BackChevronIcon = () => (
	<svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
		<path d="M7 1L1.4 7L7 13" stroke="#454545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const ContinueButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	background: ${CUSTOM_COLORS.PRIMARY};
	border: none;
	width: 100%;

	&:hover {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.9;
	}
	&:disabled {
		background: #ebebe4 !important;
		opacity: 0.9;
	}
`;

const GoogleButton = styled(Button)`
	height: 36px;
	border-radius: 20px;
	font-size: 0.9rem;
	font-weight: 500;
	border: 1px solid #d9d9d9;
	background: white;
	color: #333;
	width: 100%;

	&:hover {
		border-color: ${CUSTOM_COLORS.PRIMARY};
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const ErrorMessage = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 12px 14px;
	border-radius: 8px;
	background: #fff1f0;
	border: 1px solid #ffa39e;
	color: #a8071a;
	margin-bottom: 16px;

	.error-icon {
		font-size: 18px;
		color: #cf1322;
		margin-top: 2px;
	}

	.error-content {
		display: flex;
		flex-direction: column;
	}

	.error-title {
		font-weight: 600;
		margin-bottom: 2px;
	}

	.error-message {
		font-size: 13px;
	}
`;

const LoginLink = styled(Link)`
	color: var(--antd-primary);
	font-size: 0.875rem;
	text-decoration: none;

	&:hover {
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

const Register: React.FC = () => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { login } = useAuth();
	const inviteToken = searchParams.get("inviteToken");
	const inviteEmail = searchParams.get("inviteEmail");
	const [loading, setLoading] = useState(false);
	const [isOtpSuccess, setIsOtpSuccess] = useState(false);
	const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const lastAutoSubmittedCodeRef = useRef<string | null>(null);
	const otpSuccessTimeoutRef = useRef<number | null>(null);
	const redirectTimeoutRef = useRef<number | null>(null);

	const clearOtpTimeout = () => {
		if (otpSuccessTimeoutRef.current) {
			clearTimeout(otpSuccessTimeoutRef.current);
			otpSuccessTimeoutRef.current = null;
		}
	};

	const clearRedirectTimeout = () => {
		if (redirectTimeoutRef.current) {
			clearTimeout(redirectTimeoutRef.current);
			redirectTimeoutRef.current = null;
		}
	};

	const clearAllTimeouts = () => {
		clearOtpTimeout();
		clearRedirectTimeout();
	};

	const returnTo = searchParams.get("returnTo") || "/login";

	// Get current step from URL
	const currentStep = searchParams.get("step") || (inviteToken ? "username" : "email");

	// State variables
	const [verificationCode, setVerificationCode] = useState("");
	const [canResendCode, setCanResendCode] = useState(true);
	const [countdown, setCountdown] = useState(0);
	const [email, setEmail] = useState("");
	const [isEmailValid, setIsEmailValid] = useState(false);
	const [isCheckingEmail, setIsCheckingEmail] = useState(false);
	const [emailExists, setEmailExists] = useState<boolean | null>(null);
	const [username, setUsername] = useState("");
	const [isCheckingUsername, setIsCheckingUsername] = useState(false);
	const [usernameExists, setUsernameExists] = useState<boolean | null>(null);
	const [isUsernameValid, setIsUsernameValid] = useState(false);
	const [tiktokUsername, setTiktokUsername] = useState("");
	const [permissions, setPermissions] = useState({
		profileInfo: true,
		userAnalytics: true,
		publicVideos: true,
		videoAnalytics: true,
		username: true,
		engagementStats: true,
		accountType: true,
	});
	const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
	const [resendCount, setResendCount] = useState(0);
	/** Invite flow only — held until bank-details Save creates the user. Persisted in
	 *  sessionStorage so a refresh/hot-reload on the bank-details step doesn't lose it. */
	const pendingRegStorageKey = inviteToken ? `creator-invite:${inviteToken}:pending-registration` : null;
	const [pendingCreatorRegistration, setPendingCreatorRegistrationState] =
		useState<PendingCreatorRegistration | null>(() => {
			if (!pendingRegStorageKey) return null;
			try {
				const raw = sessionStorage.getItem(pendingRegStorageKey);
				return raw ? (JSON.parse(raw) as PendingCreatorRegistration) : null;
			} catch {
				return null;
			}
		});
	const setPendingCreatorRegistration = (payload: PendingCreatorRegistration | null) => {
		setPendingCreatorRegistrationState(payload);
		if (!pendingRegStorageKey) return;
		if (payload) {
			sessionStorage.setItem(pendingRegStorageKey, JSON.stringify(payload));
		} else {
			sessionStorage.removeItem(pendingRegStorageKey);
		}
	};

	// Load data from localStorage on component mount
	useEffect(() => {
		if (inviteEmail) {
			setEmail(inviteEmail);
			setIsEmailValid(true);
			return;
		}
		const savedEmail = localStorage.getItem("registration_email");
		const savedUsername = localStorage.getItem("registration_username");
		const savedTiktokUsername = localStorage.getItem("tiktok_user_name");

		if (savedEmail) {
			setEmail(savedEmail);
			setIsEmailValid(true);
		}
		if (savedUsername) {
			setUsername(savedUsername);
		}
		if (savedTiktokUsername) {
			setTiktokUsername(savedTiktokUsername);
		}
	}, [inviteEmail]);

	// Helper function to update URL and save data
	const updateStep = (step: string, data?: { email?: string; username?: string }) => {
		// Save to localStorage
		if (data?.email) {
			localStorage.setItem("registration_email", data.email);
		}
		if (data?.username) {
			localStorage.setItem("registration_username", data.username);
		}

		// Update URL
		setErrorMessage("");
		const params = new URLSearchParams({ step });
		if (inviteToken) params.set("inviteToken", inviteToken);
		if (inviteEmail) params.set("inviteEmail", inviteEmail);
		navigate(`/register?${params.toString()}`, { replace: true });
	};

	// Helper function to clear registration data
	const clearRegistrationData = () => {
		localStorage.removeItem("registration_email");
		localStorage.removeItem("registration_username");
	};

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
			return () => clearTimeout(timer);
		} else {
			setCanResendCode(true);
		}
	}, [countdown]);

	useEffect(() => {
		if (currentStep === "bank-details" && (!inviteToken || !pendingCreatorRegistration)) {
			setErrorMessage("Your registration session has expired. Please open the invitation link again.");
		}
	}, [currentStep, inviteToken, pendingCreatorRegistration]);

	// The bank-details card has a fixed height and an absolutely positioned Back link,
	// so surface errors as a toast instead of the in-flow banner (which would overlap and scroll).
	// The shared key replaces (not stacks) an identical toast fired directly by handlers.
	useEffect(() => {
		if (currentStep === "bank-details" && errorMessage) {
			message.error({ content: errorMessage, key: "bank-details-error" });
		}
	}, [currentStep, errorMessage]);

	useEffect(() => {
		return () => {
			clearAllTimeouts();
		};
	}, []);

	useEffect(() => {
		if (currentStep !== "verify-code" || loading) {
			return;
		}

		const normalizedCode = verificationCode.replace(/\D/g, "").slice(0, 6);

		if (normalizedCode.length === 6 && lastAutoSubmittedCodeRef.current !== normalizedCode) {
			lastAutoSubmittedCodeRef.current = normalizedCode;
			form.submit();
			return;
		}

		if (normalizedCode.length < 6) {
			lastAutoSubmittedCodeRef.current = null;
		}
	}, [currentStep, verificationCode, loading, form]);

	const handleResendCode = async () => {
		setLoading(true);
		try {
			await userService.resendVerificationEmail(email);
			message.success("Verification code sent to your email!");
			setCanResendCode(false);
			setCountdown(60); // 60 seconds cooldown
			setErrorMessage("");
			setVerificationCode("");
			setResendCount((prev) => prev + 1);
		} catch (error: any) {
			const errMsg = error?.response?.data?.message || "Failed to send code. Please try again.";
			setErrorMessage(errMsg);
		} finally {
			setLoading(false);
		}
	};

	const handleCodeComplete = (code: string) => {
		const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
		setVerificationCode(normalizedCode);
	};

	const handleCodeChange = (code: string) => {
		const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
		setVerificationCode(normalizedCode);
		if (normalizedCode.length > 0) setErrorMessage("");
	};

	const handleStepBack = () => {
		clearAllTimeouts();
		setErrorMessage("");
		setVerificationCode("");
		setIsOtpSuccess(false);
		setIsOtpSubmitting(false);
		lastAutoSubmittedCodeRef.current = null;
		setResendCount((value) => value + 1);
	};

	const onFinish = async (values: any) => {
		if (currentStep === "email") {
			// First step: validate email and request verification code
			setLoading(true);
			try {
				const email = normalizeEmail(values.email);
				setEmail(email);
				await userService.resendVerificationEmail(email);
				updateStep("verify-code", { email });
				setCanResendCode(false);
				setCountdown(60);
				message.success("Verification code sent to your email!");
			} catch (error: any) {
				const errMsg = error?.response?.data?.message || "Failed to send verification code. Please try again.";
				setErrorMessage(errMsg);
				return; // do not proceed to next step on error
			} finally {
				setLoading(false);
			}
		} else if (currentStep === "verify-code") {
			// Second step: Verify code and proceed to username selection
			setLoading(true);
			setIsOtpSubmitting(true);
			try {
				await userService.verifyEmail(email, verificationCode);
				// Show success animation consistent with login flow
				setIsOtpSuccess(true);
				// Wait for animation to run, then advance to username step.
				// Use a ref-backed timeout so it can be cleared if the component
				// unmounts or the user navigates away quickly.
				clearOtpTimeout();
				otpSuccessTimeoutRef.current = window.setTimeout(() => {
					updateStep("username");
					message.success("Code verified! Now choose your username.");
					setIsOtpSuccess(false);
				}, 800);
			} catch {
				setErrorMessage("Invalid code");
				setVerificationCode("");
				setResendCount((prev) => prev + 1);
				lastAutoSubmittedCodeRef.current = null;
				setIsOtpSuccess(false);
			} finally {
				setIsOtpSubmitting(false);
				setLoading(false);
			}
		} else if (currentStep === "username") {
			// Third step: Proceed to social connect
			setLoading(true);
			try {
				// Simulate API call
				await new Promise((resolve) => setTimeout(resolve, 1000));
				updateStep("connect-social", { username: values.username });
				message.success("Username saved! Now connect your socials.");
			} catch (error) {
				setErrorMessage("Username already taken. Please try another.");
			} finally {
				setLoading(false);
			}
		} else if (currentStep === "connect-social") {
			// Continue should move to next step (no TikTok redirect here)
			setLoading(true);
			try {
				updateStep("upload-avatar");
				message.success("Great! You can add a profile picture next.");
			} finally {
				setLoading(false);
			}
		} else if (currentStep === "tiktok-auth") {
			// Fifth step: Proceed to password setup (after TikTok redirect)
			setLoading(true);
			try {
				await new Promise((resolve) => setTimeout(resolve, 1000));
				updateStep("upload-avatar");
				message.success("TikTok connected! Optionally upload a profile picture.");
			} catch (error) {
				message.error("Failed to connect to TikTok. Please try again.");
			} finally {
				setLoading(false);
			}
		}
	};

	const handlePasswordSubmit = async (password: string) => {
		setLoading(true);
		try {
			// Build payload without storing password in localStorage. The TOS
			// approval was already enforced in PasswordSetupStep — we re-send it
			// here so the backend can persist a legally-auditable consent record
			// (version + IP + user-agent) tied to the newly created user.
			const payload = {
				email,
				password,
				name: username,
				roles: ["user"],
				profileImage: avatarDataUrl || undefined,
				tosAccepted: true,
				tosVersion: CURRENT_TOS_VERSION,
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			};
			if (inviteToken) {
				// Defer DB account creation until bank-details Save / Skip.
				setPendingCreatorRegistration({
					...payload,
					documentTypes: ["terms_of_service", "fee_schedule", "privacy_policy"],
				});
				updateStep("bank-details");
				return;
			}
			await userService.register(payload);

			message.success("Registration successful! Redirecting to login...");

			// Clear registration data after successful registration
			clearRegistrationData();

			// Redirect after 2 seconds
			clearRedirectTimeout();
			redirectTimeoutRef.current = window.setTimeout(() => {
				navigate("/login");
			}, 2000);
		} catch (error) {
			const errMsg =
				(error as any)?.response?.data?.message || "Failed to complete registration. Please try again.";
			setErrorMessage(errMsg);
		} finally {
			setLoading(false);
		}
	};

	const finalizeCreatorRegistration = async (bank: BankDetailsValues) => {
		if (!inviteToken || !pendingCreatorRegistration) {
			const expiredMsg = "Your registration session has expired. Please open the invitation link again.";
			setErrorMessage(expiredMsg);
			// Direct toast too: setErrorMessage(same value) doesn't re-fire the effect.
			message.error({ content: expiredMsg, key: "bank-details-error" });
			return;
		}
		setLoading(true);
		setErrorMessage("");
		try {
			const paymentProfile = {
				country: bank.country,
				bankName: bank.bankName.trim(),
				accountName: bank.accountName.trim(),
				accountNumber: bank.accountNumber.trim(),
				swiftBic: bank.swiftBic?.trim() || undefined,
				currency: bank.currency?.trim() || undefined,
			};
			const result = await creatorInvitationApi.register(inviteToken, {
				...pendingCreatorRegistration,
				paymentProfile,
			});
			markCreatorWelcomePending(result.user.email);
			login(result.user);
			sessionStorage.removeItem(`creator-invite:${inviteToken}:legal-accepted`);
			clearRegistrationData();
			setPendingCreatorRegistration(null);

			message.success("Account created and bank details saved.");
			navigate("/campaigns", { replace: true });
		} catch (error) {
			const errMsg =
				(error as any)?.response?.data?.message || "Failed to complete registration. Please try again.";
			setErrorMessage(errMsg);
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleSignUp = () => {
		if (inviteToken) {
			message.info("Complete signup with the invited email and password.");
			return;
		}
		const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
		const redirectUri = `${window.location.origin}/auth/callback`;
		const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
		url.searchParams.set("client_id", clientId);
		url.searchParams.set("redirect_uri", redirectUri);
		url.searchParams.set("response_type", "code");
		url.searchParams.set("scope", "openid email profile");
		url.searchParams.set("prompt", "select_account");
		window.location.href = url.toString();
	};

	const handleTikTokConnect = async () => {
		setLoading(true);
		try {
			const tiktokAuthUrl = import.meta.env.VITE_TIKTOK_AUTH_URL;
			if (tiktokAuthUrl) {
				const redirectUrl = `${tiktokAuthUrl}&state=${encodeURIComponent(
					JSON.stringify({ step: "tiktok-auth" }),
				)};`;
				message.success("Redirecting to TikTok...");
				window.location.href = redirectUrl;
			} else {
				message.info("TikTok auth URL not configured.");
			}
		} catch (_) {
			message.error("Failed to connect to TikTok. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const validateEmail = (email: string) => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setEmail(value);
		setIsEmailValid(validateEmail(value));
		// Persist email as user types
		localStorage.setItem("registration_email", value);
	};

	const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setUsername(value);
		// Persist username as user types
		localStorage.setItem("registration_username", value);
	};

	// Debounce username availability check
	useEffect(() => {
		if (!username) {
			setUsernameExists(null);
			setIsUsernameValid(false);
			return;
		}

		const isValid = /^[a-zA-Z0-9_]{3,20}$/.test(username);
		setIsUsernameValid(isValid);
		if (!isValid) {
			setUsernameExists(null);
			return;
		}

		const handle = setTimeout(async () => {
			try {
				setIsCheckingUsername(true);
				const res = await userService.checkUsernameExists(username);
				setUsernameExists(Boolean(res?.username));
			} catch (_) {
				setUsernameExists(null);
			} finally {
				setIsCheckingUsername(false);
			}
		}, 0);
		return () => clearTimeout(handle);
	}, [username]);

	const handlePermissionToggle = (permission: keyof typeof permissions) => {
		setPermissions((prev) => ({
			...prev,
			[permission]: !prev[permission],
		}));
	};

	const titleGenerator = () => {
		if (currentStep === "bank-details") {
			return undefined;
		} else if (currentStep === "set-password" || currentStep === "verify-code") {
			return "";
		} else if (currentStep === "upload-avatar") {
			return "Add a profile picture (optional)";
		} else if (currentStep === "tiktok-auth") {
			return "Creatorverse wants to access your TikTok account";
		} else if (currentStep === "connect-social") {
			return "";
		} else if (currentStep === "username") {
			return (
				<span
					style={{
						display: "inline-flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "0.5rem",
						maxWidth: "100%",
					}}
				>
					<span
						style={{
							display: "inline-block",
							whiteSpace: "nowrap",
							fontSize: "clamp(1.05rem, 4.8vw, 1.55rem)",
							lineHeight: 1.2,
						}}
					>
						Choose your username
					</span>
				</span>
			);
		} else {
			return <>Start your journey on Creatorverse</>;
		}
	};

	const subtitleGenerator = () => {
		if (currentStep === "bank-details") {
			return "";
		} else if (currentStep === "set-password") {
			return (
				<div>
					<div style={{ fontSize: "24px", fontWeight: "600", color: "#333", lineHeight: "30px" }}>
						<p style={{ marginBottom: 0, marginTop: 0 }}>Success!</p>
						<p style={{ marginBottom: "1.5rem", marginTop: 0 }}>Your Page is Secured & Connected</p>
					</div>

					<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
							<div
								style={{
									flex: 1,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div style={{ fontSize: "14px", color: "#333" }}>
									<span style={{ color: "#00000040" }}>Creatorverse</span>/{username || "username"}
								</div>
								<div
									style={{
										color: username ? "#2DCCD3" : "#ff4d4f",
										fontWeight: 500,
										display: "flex",
										alignItems: "center",
										gap: "0.5rem",
										fontSize: 14,
									}}
								>
									<span>{username ? "Secured" : "Not Secured"}</span>
								</div>
							</div>
							<span
								style={{
									color: username ? "#2DCCD3" : "#ff4d4f",
									fontWeight: "bold",
									minWidth: 20,
									textAlign: "center",
								}}
							>
								{username ? "✓" : "✗"}
							</span>
						</div>

						<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
							<div
								style={{
									flex: 1,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#333" }}>
									<SiTiktok style={{ color: "#000", fontSize: 18 }} />
									<span style={{ fontSize: "14px" }}>{tiktokUsername || "Not connected"}</span>
								</div>
								<div
									style={{
										color: tiktokUsername ? "#2DCCD3" : "#ff4d4f",
										fontWeight: 500,
										fontSize: 14,
									}}
								>
									{tiktokUsername ? "Linked" : "Not Linked"}
								</div>
							</div>
							<span
								style={{
									color: tiktokUsername ? "#2DCCD3" : "#ff4d4f",
									fontWeight: "bold",
									minWidth: 20,
									textAlign: "center",
								}}
							>
								{tiktokUsername ? "✓" : "✗"}
							</span>
						</div>
					</div>

					<div
						style={{
							fontSize: "1.25rem",
							fontWeight: "600",
							color: "#333",
							marginBottom: "0.5rem",
							marginTop: "2rem",
						}}
					>
						Just One Last Step
					</div>
					<div style={{ color: "#666", fontSize: "0.9rem", marginBottom: "0rem" }}>Let's add a password</div>
				</div>
			);
		} else if (currentStep === "upload-avatar") {
			return "You can skip this step and set your picture later.";
		} else if (currentStep === "tiktok-auth") {
			return "";
		} else if (currentStep === "connect-social") {
			return "";
		} else if (currentStep === "username") {
			return "Choose your Constellation / Star";
		} else if (currentStep === "verify-code") {
			return "";
		} else {
			return "Sign Up for free today!";
		}
	};

	if (inviteToken) {
		const params = new URLSearchParams({ inviteToken });
		if (inviteEmail) params.set("inviteEmail", inviteEmail);
		return <Navigate to={`/creator-register?${params.toString()}`} replace />;
	}

	return (
		<AuthLayout
			title={titleGenerator()}
			subtitle={subtitleGenerator()}
			backLinkTo={returnTo}
			onBack={handleStepBack}
			backLinkLabel={
				currentStep === "bank-details" ? (
					<>
						<BackChevronIcon /> Back
					</>
				) : undefined
			}
			bankDetails={currentStep === "bank-details"}
		>
			<AnimatePresence mode="wait" initial={false}>
				<StepTransition
					key={`register-step-${currentStep}`}
					initial={{ opacity: 0, x: 18 }}
					animate={{ opacity: 1, x: 0 }}
					exit={{ opacity: 0, x: -12 }}
					transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
				>
					{currentStep === "connect-social" && (
						<div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
							<img src="/Register_Social.png" width={250} alt="Connect your socials illustration" />
							<h2 style={{ fontWeight: 600, marginBottom: 0 }}>Connect your socials</h2>
							<p style={{ fontSize: 14, textAlign: "center", width: 268 }}>
								Power your creator journey - connect, analyze, and monetize
							</p>
						</div>
					)}
				</StepTransition>
			</AnimatePresence>
			<div
				style={{
					display: "block",
					minHeight:
						errorMessage === "" || currentStep === "bank-details" || currentStep === "verify-code"
							? 0
							: 100,
				}}
			>
				<AnimatePresence mode="wait" initial={false}>
					{!!errorMessage && currentStep !== "bank-details" && currentStep !== "verify-code" && (
						<StepTransition
							key="register-error"
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<ErrorMessage>
								<CloseCircleOutlined className="error-icon" />
								<div className="error-content">
									<div className="error-title">Error</div>
									<div className="error-message">{errorMessage}</div>
								</div>
							</ErrorMessage>
						</StepTransition>
					)}
				</AnimatePresence>

				<AnimatePresence mode="wait" initial={false}>
					{currentStep === "verify-code" && (
						<StepTransition
							key="verify-code-header"
							initial={{ opacity: 0, y: -6 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
						>
							<div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
								<h2
									style={{
										margin: "0 0 1rem 0",
										fontSize: "24px",
										fontWeight: "600",
										color: "#000",
									}}
								>
									Confirm your email
								</h2>
								<p style={{ lineHeight: "20px", margin: 0 }}>
									We sent a temporary sign up code to{" "}
									<strong style={{ color: CUSTOM_COLORS.PRIMARY, fontWeight: 600 }}>{email}</strong>
								</p>
							</div>
						</StepTransition>
					)}
				</AnimatePresence>
			</div>
			{/* Bank details has its own <Form>; render it outside StyledForm to avoid
			    invalid nested <form> elements (which break its submit/validation). */}
			{currentStep === "bank-details" && (
				<AnimatePresence mode="wait" initial={false}>
					<StepTransition
						key="register-step-bank-details"
						initial={{ opacity: 0, x: 18 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -12 }}
						transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
					>
						<BankDetailsStep onSubmit={(values) => finalizeCreatorRegistration(values)} loading={loading} />
					</StepTransition>
				</AnimatePresence>
			)}
			<StyledForm form={form} name="register" onFinish={onFinish} layout="vertical">
				<div>
					<AnimatePresence mode="wait" initial={false}>
						<StepTransition
							key={`register-step-${currentStep}`}
							initial={{ opacity: 0, x: 18 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -12 }}
							transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
						>
							<div>
								{currentStep === "email" && (
									<EmailStep
										isEmailValid={isEmailValid}
										onEmailChange={handleEmailChange}
										onMetaChange={({ checking, exists }) => {
											setIsCheckingEmail(checking);
											setEmailExists(exists);
										}}
										onError={(msg) => setErrorMessage(msg)}
									/>
								)}

								{currentStep === "verify-code" && (
									<CodeVerificationStep
										resetSignal={resendCount}
										onCodeComplete={handleCodeComplete}
										onCodeChange={handleCodeChange}
										onResendCode={handleResendCode}
										canResendCode={canResendCode}
										loading={loading}
										countdown={countdown}
										isSuccess={isOtpSuccess}
										isSubmitting={isOtpSubmitting}
										error={errorMessage}
									/>
								)}

								{currentStep === "username" && (
									<UsernameStep
										onUsernameChange={handleUsernameChange}
										onMetaChange={({ checking, exists, isValid }) => {
											setIsCheckingUsername(checking);
											setUsernameExists(exists);
											setIsUsernameValid(isValid);
										}}
										exists={usernameExists}
										checking={isCheckingUsername}
									/>
								)}

								{currentStep === "connect-social" && (
									<>
										<SocialConnectStep onConnect={handleTikTokConnect} />
									</>
								)}

								{currentStep === "tiktok-auth" && (
									<TikTokAuthStep
										permissions={permissions}
										onPermissionToggle={handlePermissionToggle}
										loading={loading}
									/>
								)}

								{currentStep === "upload-avatar" && (
									<UploadAvatarStep
										onSkip={() => updateStep("set-password")}
										onAvatarSelected={(dataUrl) => {
											setAvatarDataUrl(dataUrl);
											updateStep("set-password");
										}}
										loading={loading}
									/>
								)}

								{currentStep === "set-password" && (
									<PasswordSetupStep
										username={username}
										onPasswordSubmit={handlePasswordSubmit}
										loading={loading}
										termsAlreadyAccepted={Boolean(inviteToken)}
									/>
								)}
							</div>
						</StepTransition>
					</AnimatePresence>

					<AnimatePresence mode="wait" initial={false}>
						<StepTransition
							key={`register-step-${currentStep}`}
							initial={{ opacity: 0, x: 18 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: -12 }}
							transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
						>
							{currentStep !== "set-password" &&
								currentStep !== "upload-avatar" &&
								currentStep !== "bank-details" &&
								currentStep !== "verify-code" && (
									<Form.Item>
										<ContinueButton
											type="primary"
											htmlType="submit"
											loading={
												loading ||
												(currentStep === "email" && isCheckingEmail) ||
												(currentStep === "username" && isCheckingUsername)
											}
											disabled={
												(currentStep === "email" &&
													(!isEmailValid ||
														isCheckingEmail ||
														emailExists === true ||
														emailExists === null)) ||
												(currentStep === "username" &&
													(!isUsernameValid || isCheckingUsername || usernameExists === true))
											}
										>
											Continue
										</ContinueButton>
									</Form.Item>
								)}
						</StepTransition>
					</AnimatePresence>

					<AnimatePresence mode="wait" initial={false}>
						{currentStep === "email" && (
							<GoogleButton icon={<FaGoogle style={{ color: "#4285F4" }} />} onClick={handleGoogleSignUp}>
								Sign Up with Google
							</GoogleButton>
						)}

						{currentStep !== "username" &&
							currentStep !== "set-password" &&
							currentStep !== "upload-avatar" &&
							currentStep !== "connect-social" &&
							currentStep !== "bank-details" &&
							currentStep !== "tiktok-auth" && (
								<div
									style={{
										textAlign: "center",
										marginTop: "1rem",
										marginBottom: "4rem",
										fontSize: "0.875rem",
									}}
								>
									Already have an account? <LoginLink to="/login">Log in</LoginLink>
								</div>
							)}
					</AnimatePresence>
				</div>
			</StyledForm>
		</AuthLayout>
	);
};

export default Register;
